/* eslint-disable react/prop-types */
import { Auth0Provider } from '@auth0/auth0-react';
import { ThemeProvider } from '@mui/material/styles';
import {
    QueryClient,
    QueryClientProvider,
} from '@tanstack/react-query';
import React, {
    StrictMode,
} from 'react';
import { createRoot } from 'react-dom/client';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import {
    BrowserRouter,
    useNavigate,
} from 'react-router-dom';
import 'regenerator-runtime';

import * as routes from './constants/routes';
import {
    MCO_THEME,
    POL_THEME,
    ThemeContext,
} from './constants/theme.ts';
import { AppProvider } from './contexts/AppContext';
import muiTheme from './theme/index.ts';

import AppContainer from './components/layout/AppContainer';

import './_theme.scss';

const POL_TAGMANAGER_CONFIG = {
    auth: process.env.POL_TAGMANAGER_AUTH,
    gtmId: process.env.POL_TAGMANAGER_ID,
    preview: process.env.POL_TAGMANAGER_ENV,
};
const MCO_TAGMANAGER_CONFIG = {
    auth: process.env.MCO_TAGMANAGER_AUTH,
    gtmId: process.env.MCO_TAGMANAGER_ID,
    preview: process.env.MCO_TAGMANAGER_ENV,
};

const { hostname } = window.location;
const hostnameToLowerCase = hostname.toLowerCase().replace('www.', '');

const themeName = hostnameToLowerCase === process.env.MCO_HOSTNAME
    ? MCO_THEME
    : POL_THEME;

if (process.env.PUBLISH_ENV !== 'development') {
    const isPOL = themeName === POL_THEME;
    const isMCO = themeName === MCO_THEME;

    if (isPOL || isMCO) {
        let tagManagerConfig = null;
        let analyticsID = null;

        if (isPOL) {
            tagManagerConfig = POL_TAGMANAGER_CONFIG;
            analyticsID = process.env.POL_GA4_ID;
        } else if (isMCO) {
            tagManagerConfig = MCO_TAGMANAGER_CONFIG;
            analyticsID = process.env.MCO_GA4_ID;
        }

        if (tagManagerConfig) {
            ReactGA.initialize(analyticsID, {
                gaOptions: {
                    send_page_view: false,
                },
            });
            TagManager.initialize(tagManagerConfig);

            // Add noindex meta tag to prevent search indexing if env is stage
            if (process.env.PUBLISH_ENV === 'stage') {
                const metaTag = document.createElement('meta');
                metaTag.setAttribute('name', 'robots');
                metaTag.setAttribute('content', 'noindex');
                document.getElementsByTagName('head')[0].appendChild(metaTag);
            }
        }
    }
}

const Auth0ProviderWithRedirectCallback = ({ children, ...props }) => {
    const navigate = useNavigate();
    const onRedirectCallback = (appState) => {
        navigate(appState && appState.returnTo ? appState.returnTo : routes.HOME);
    };
    return (
        <Auth0Provider onRedirectCallback={onRedirectCallback} {...props}>
            {children}
        </Auth0Provider>
    );
};

const providerConfig = {
    domain: process.env.REACT_APP_AUTH0_DOMAIN,
    clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
    authorizationParams: {
        redirect_uri: routes.AUTH_REDIRECT,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    },
    useRefreshTokens: true,
    useRefreshTokensFallback: true,
    cacheLocation: 'localstorage',
};

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            gcTime: 6 * 60 * 60 * 1000, // 6 hours
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 60 * 60 * 1000, // 1 hour
        },
    },
});

const container = document.getElementById('app');
const root = createRoot(container);
root.render((
    <StrictMode>
        <QueryClientProvider client={queryClient}>
            <ThemeContext.Provider value={themeName}>
                <ThemeProvider theme={muiTheme}>
                    <BrowserRouter>
                        <Auth0ProviderWithRedirectCallback {...providerConfig}>
                            <AppProvider>
                                <AppContainer />
                            </AppProvider>
                        </Auth0ProviderWithRedirectCallback>
                    </BrowserRouter>
                </ThemeProvider>
            </ThemeContext.Provider>
        </QueryClientProvider>
    </StrictMode>
));
